import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { InsertIdentityMode } from '../../common/utils/insertStatementBuilder';
import { fetchIdentityColumns } from '../../common/utils/identityColumns';

/**
 * Table Explorer — identity-column derivation (D3, fix round 2).
 *
 * Extracted out of `TableExplorerTab.tsx` as a pure module (zero behavior
 * change from the inline memos it replaces) so unit tests exercise the real
 * production logic instead of a hand-copied stand-in that could silently
 * drift from the component.
 */

/**
 * TE's authoritative identity-column set — metadata-first, Blueprint-sidecar
 * fallback. Drives the column-enrichment effect (`isIdentity` stamped onto
 * every grid column), edit-mode gating, and INSERT copy/export.
 *
 * Mirrors `effectiveIdentityColumnsSet` in `TableExplorerTab.tsx` exactly:
 * uses the live-fetched `identityColumnsSet` whenever it has anything, and
 * only falls back to the Blueprint sidecar's identity columns when in
 * blueprint-edit mode AND metadata came back empty (e.g. the linked DB is
 * disconnected, or the metadata query is disabled) — a scenario metadata
 * alone can never resolve.
 */
export function computeEffectiveIdentityColumnsSet(
  isBlueprintEdit: boolean,
  identityColumnsSet: Set<string>,
  blueprintIdentityColumns: readonly string[],
): Set<string> {
  if (isBlueprintEdit && identityColumnsSet.size === 0) return new Set(blueprintIdentityColumns);
  return identityColumnsSet;
}

/**
 * Identity column NAMES for the INSERT builder (`insertStatementBuilder`'s
 * `identityColumns` option), read off the grid's already-enriched columns —
 * `isIdentity`, stamped from `computeEffectiveIdentityColumnsSet` above by the
 * column-enrichment effect.
 *
 * D1 — this snapshot is authoritative ONLY in blueprint-edit mode, where the
 * Blueprint sidecar (not a live catalog query) is the source of truth. On a
 * live connection it is stamped asynchronously by `useTableMetadata`, which is
 * a different query from the one that puts rows on screen — see
 * `resolveTableExplorerIdentityOptions` below.
 */
export function computeInsertIdentityColumns(
  columns: readonly Pick<TableExplorerColumn, 'name' | 'isIdentity'>[],
): string[] {
  return columns.filter((c) => c.isIdentity).map((c) => c.name);
}

export type TableExplorerIdentityResolution = {
  options: { identityColumns?: string[]; identityMode: InsertIdentityMode };
  /**
   * `true` → the identity check itself could not be completed, and it could
   * have changed the emitted SQL. Callers must warn instead of silently
   * emitting a possibly-unwrapped INSERT (Msg 544).
   */
  unreliable: boolean;
};

/**
 * D1 (codex pre-PR review) — ACTION-TIME identity resolution for TE's
 * "Copy as INSERT" / "Export as SQL", mirroring the Query Editor grid's
 * `resolveIdentityOptions` (`common/hooks/useExportCopy.ts`) so both grids
 * answer "which columns are IDENTITY?" the same way.
 *
 * Why not the enriched-columns snapshot: Copy/Export are enabled as soon as
 * ROWS are present, and rows (`useTableData`) and metadata (`useTableMetadata`)
 * are two independent queries — neither gates on the other. Metadata is the
 * heavier call (`detailed: true` → enrichments + constraints + indexes), so
 * "rows on screen, identity not yet stamped" is the expected ordering. Worse,
 * `useTableMetadata` turns a `success:false` response into `undefined` rather
 * than an error, so the snapshot could stay empty forever with nothing to warn
 * from. Either way the old path emitted an unwrapped INSERT — the exact Msg 544
 * failure this feature exists to prevent.
 *
 * Blueprint-edit mode keeps reading the enriched columns: there the sidecar is
 * authoritative and the linked DB may be offline, so a catalog fetch is both
 * wrong and impossible (preserves the fix in commit `8bfb8b434`).
 */
export async function resolveTableExplorerIdentityOptions(args: {
  skipIdentity: boolean;
  engineId: string | undefined;
  isBlueprintEdit: boolean;
  connectionId?: string | null;
  database?: string | null;
  schema?: string;
  table: string;
  columns: readonly Pick<TableExplorerColumn, 'name' | 'isIdentity'>[];
}): Promise<TableExplorerIdentityResolution> {
  const identityMode: InsertIdentityMode = args.skipIdentity ? 'exclude' : 'include-wrapped';

  // `identityMode` always rides along (the export request records the user's
  // choice), but identity NAMES can only change the output when the toggle is
  // on or the engine is MSSQL — same gate as the QE grid, so non-MSSQL copies
  // keep skipping the catalog round-trip entirely.
  if (!args.skipIdentity && args.engineId !== 'mssql') return { options: { identityMode }, unreliable: false };

  if (args.isBlueprintEdit) {
    const identityColumns = computeInsertIdentityColumns(args.columns);
    return {
      options: identityColumns.length > 0 ? { identityColumns, identityMode } : { identityMode },
      unreliable: false,
    };
  }

  const identityColumns = await fetchIdentityColumns({
    connectionId: args.connectionId,
    database: args.database,
    schema: args.schema,
    table: args.table,
  });
  // `null` = the table is known but the check failed (no connection context,
  // `success:false`, throw). Never conflate with "checked, found none" (`[]`).
  if (identityColumns === null) return { options: { identityMode }, unreliable: true };
  if (identityColumns.length === 0) return { options: { identityMode }, unreliable: false };

  return { options: { identityColumns, identityMode }, unreliable: false };
}
